<template>
  <div class="login-container">
    <div class="login-form-container">
      <!-- Logo Section -->
      <div class="logo-section">
        <div class="logo-wrapper">
          <img src="@/assets/logo.png" alt="QUALI-D" class="logo" />
        </div>
        <div class="meta-partner-badge">
          <picture>
            <source
              srcset="https://quali-d.com/resources/Meta_Business_Partners_inline_lockup_positive_primary_RGB.png"
              media="(prefers-color-scheme: dark)"
            />
            <img
              src="https://quali-d.com/resources/Meta_Business_Partners_inline_lockup_negative_primary_RGB.png"
              alt="Meta Business Partner"
              class="meta-partner-logo"
            />
          </picture>
        </div>
      </div>

      <!-- Login Form -->
      <div class="form-section">
        <div class="form-header">
          <h2 class="form-title">Login</h2>
        </div>

        <form @submit.prevent="handleLogin" class="login-form">
          <div class="form-group">
            <label for="phone" class="form-label">Phone Number</label>
            <InternationalPhoneInput
              v-model="phoneData"
              placeholder="01112223334"
              :has-error="!!errors.phone"
            />
            <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
          </div>

          <div class="form-group">
            <label for="password" class="form-label">Password</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <circle cx="12" cy="7" r="4"/>
                <path d="m8 11 0-4a4 4 0 0 1 8 0v4"/>
              </svg>
              <input
                id="password"
                v-model="loginForm.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Enter your password"
                class="form-input"
                :class="{ 'error': errors.password }"
                required
              />
              <button
                type="button"
                @click="togglePassword"
                class="toggle-password"
              >
                <svg v-if="!showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              </button>
            </div>
            <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
          </div>

          <div class="form-options">
            <router-link to="/forgot-password" class="forgot-link">Forgot password?</router-link>
          </div>

          <button
            type="submit"
            class="login-button"
            :disabled="isLoading"
            :class="{ 'loading': isLoading }"
          >
            <span v-if="!isLoading">Sign In</span>
            <span v-else class="loading-spinner">
              <span class="spinner"></span>
              Signing in...
            </span>
          </button>

          <router-link to="/register" class="register-button">
            Register ⭐
          </router-link>

          <router-link to="/agent-login" class="agent-login-button">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            Agent Login
          </router-link>

          <router-link to="/demo" class="demo-button">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            Try Demo
          </router-link>

          <div v-if="successMessage" class="success-message">
            {{ successMessage }}
          </div>

          <div v-if="loginError" class="login-error">
            {{ loginError }}
          </div>
        </form>

        <div class="privacy-footer">
          <p class="privacy-text">
            By using Quali-D, you agree to our
            <a
              href="https://quali-d.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              class="privacy-link"
            >Privacy Policy</a>
          </p>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import { authStore } from '@/store/auth'
import { AuthService } from '@/services/auth'
import { DEFAULT_COUNTRY_CODE } from '@/utils/countryCodes'
import InternationalPhoneInput from '@/components/InternationalPhoneInput.vue'

export default {
  name: 'LoginView',
  components: {
    InternationalPhoneInput
  },
  data () {
    return {
      phoneData: {
        countryCode: DEFAULT_COUNTRY_CODE,
        phone: ''
      },
      loginForm: {
        password: ''
      },
      errors: {},
      showPassword: false,
      isLoading: false,
      loginError: '',
      successMessage: ''
    }
  },
  methods: {
    togglePassword () {
      this.showPassword = !this.showPassword
    },
    validateForm () {
      this.errors = {}
      let isValid = true

      if (!this.phoneData.phone.trim()) {
        this.errors.phone = 'Phone number is required'
        isValid = false
      } else if (!/^[0-9+\-\s()]+$/.test(this.phoneData.phone)) {
        this.errors.phone = 'Please enter a valid phone number'
        isValid = false
      }

      if (!this.loginForm.password.trim()) {
        this.errors.password = 'Password is required'
        isValid = false
      } else if (this.loginForm.password.length < 6) {
        this.errors.password = 'Password must be at least 6 characters'
        isValid = false
      }

      return isValid
    },
    async handleLogin () {
      this.loginError = ''

      if (!this.validateForm()) {
        return
      }

      this.isLoading = true

      try {
        console.log('=== Starting Login Process ===')

        // Special case for Egypt: use "+2" instead of "+20"
        const countryCode = this.phoneData.countryCode === '20' ? '2' : this.phoneData.countryCode

        const loginResponse = await authStore.login({
          country_code: countryCode,
          phone: this.phoneData.phone,
          password: this.loginForm.password
        })

        console.log('Login response:', loginResponse)
        console.log('Auth store state after login:', {
          user: authStore.user,
          isAuthenticated: authStore.isAuthenticated
        })

        // Check authentication status
        const isAuth = AuthService.isAuthenticated()
        console.log('AuthService.isAuthenticated():', isAuth)

        if (isAuth) {
          console.log('Authentication successful, redirecting to dashboard...')
          await this.$router.push('/')
          console.log('Redirect completed')
        } else {
          console.log('Authentication failed - not redirecting')
          throw new Error('Authentication verification failed')
        }
      } catch (error) {
        console.error('Login error in component:', error)
        this.loginError = error.message || 'Login failed. Please try again.'
      } finally {
        this.isLoading = false
      }
    }
  },
  mounted () {
    // Check for success message from forgot password flow
    const urlParams = new URLSearchParams(window.location.search)
    const message = urlParams.get('message')
    if (message) {
      this.successMessage = decodeURIComponent(message)
      // Clear the URL parameter
      const url = new URL(window.location)
      url.searchParams.delete('message')
      window.history.replaceState({}, document.title, url.pathname)
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  position: relative;
  overflow: hidden;
}

.login-form-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 48px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 480px;
  margin: 20px;
  position: relative;
  z-index: 10;
}

.logo-section {
  text-align: center;
  margin-bottom: 48px;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
}

.logo {
  height: 48px;
  width: auto;
}

.brand-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.brand-subtitle {
  color: #6b7280;
  font-size: 16px;
  margin: 0;
  font-weight: 500;
}

.form-section {
  width: 100%;
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.form-title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px;
}

.form-subtitle {
  color: #6b7280;
  font-size: 16px;
  margin: 0;
}

.login-form {
  width: 100%;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  width: 18px;
  height: 18px;
  opacity: 0.6;
  z-index: 2;
  color: #6b7280;
}

.form-input {
  width: 100%;
  padding: 16px 16px 16px 52px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  background: white;
  color: #1f2937;
  transition: all 0.2s;
  outline: none;
}

.form-input:focus {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.toggle-password {
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  padding: 4px;
  border-radius: 4px;
  transition: opacity 0.2s;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-password:hover {
  opacity: 1;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 14px;
  margin-top: 6px;
}

.form-options {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 32px;
}

.forgot-link {
  font-size: 14px;
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.forgot-link:hover {
  text-decoration: underline;
}

.login-button {
  width: 100%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

.login-button:disabled {
  opacity: 0.8;
  cursor: not-allowed;
  transform: none;
}

.register-button {
  display: block;
  width: 100%;
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
  border: 2px solid #10b981;
  margin-top: 16px;
  transition: all 0.2s;
}

.register-button:hover {
  background: #10b981;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
}

.agent-login-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  border: 2px solid #6366f1;
  margin-top: 16px;
  transition: all 0.2s;
}

.agent-login-button:hover {
  background: #6366f1;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.3);
}

.agent-login-button svg {
  width: 18px;
  height: 18px;
}

.demo-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.1);
  border: 2px solid #8b5cf6;
  margin-top: 16px;
  transition: all 0.2s;
}

.demo-button:hover {
  background: #8b5cf6;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3);
}

.demo-button svg {
  width: 18px;
  height: 18px;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-message {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #059669;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 16px;
  text-align: center;
}

.login-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 16px;
  text-align: center;
}

.privacy-footer {
  text-align: center;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.privacy-text {
  color: #6b7280;
  font-size: 13px;
  margin: 0;
}

.privacy-link {
  color: #3b82f6;
  font-weight: 700;
  text-decoration: underline;
  font-size: 13px;
}

.privacy-link:hover {
  color: #1d4ed8;
}

.form-footer {
  text-align: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.footer-text {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
}

.signup-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 600;
}

.signup-link:hover {
  text-decoration: underline;
}

.meta-partner-badge {
  text-align: center;
  margin-top: 12px;
}

.meta-partner-logo {
  display: block;
  height: 48px;
  width: auto;
  margin: 0 auto;
  opacity: 0.9;
}

@media (max-width: 640px) {
  .login-form-container {
    padding: 32px 24px;
    margin: 16px;
  }

  .brand-title {
    font-size: 24px;
  }

  .form-title {
    font-size: 24px;
  }

  .form-options {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
}
</style>
